import { QuestionOption, AnswerState } from '@/types/exam';
import { RichContent } from '@/components/content/RichContent';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface OptionsListProps {
  options: QuestionOption[];
  selectedIndex: number | null;
  correctIndex?: number;
  answerState: AnswerState;
  onSelect: (index: number) => void;
  disabled: boolean;
}

const letters = ['A', 'B', 'C', 'D', 'E', 'F'];

export function OptionsList({ options, selectedIndex, correctIndex, answerState, onSelect, disabled }: OptionsListProps) {
  const isAnswered = answerState !== 'unanswered';

  return (
    <div className="space-y-2 sm:space-y-3">
      {options.map((option, index) => {
        const isSelected = selectedIndex === index;
        const isCorrect = correctIndex === index;
        const isWrong = isAnswered && isSelected && !isCorrect;

        let stateClasses = 'bg-overlay-1 border-line hover:bg-overlay-2 hover:border-line';
        let letterClasses = 'bg-overlay-2 text-fg-subtle';
        let indicator = null;

        if (!isAnswered && isSelected) {
          stateClasses = 'bg-green-500/10 border-green-500/30';
          letterClasses = 'bg-green-500/20 text-green-400';
        } else if (isAnswered && isCorrect) {
          stateClasses = 'bg-green-500/15 border-green-500/40';
          letterClasses = 'bg-green-500/20 text-green-400';
          indicator = <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-400 flex-shrink-0" />;
        } else if (isWrong) {
          stateClasses = 'bg-red-500/15 border-red-500/40';
          letterClasses = 'bg-red-500/20 text-red-400';
          indicator = <X className="w-4 h-4 sm:w-5 sm:h-5 text-red-400 flex-shrink-0" />;
        } else if (isAnswered) {
          stateClasses = 'bg-overlay-1 border-line opacity-50';
        }

        return (
          <button
            key={index}
            onClick={() => !disabled && onSelect(index)}
            disabled={disabled}
            className={cn(
              'w-full flex items-center gap-3 p-3 sm:p-4 rounded-xl border transition-all duration-200 text-left',
              stateClasses,
              disabled && !isAnswered && 'cursor-not-allowed'
            )}
          >
            <div className={cn(
              'w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0 transition-colors',
              letterClasses
            )}>
              {letters[index]}
            </div>

            <div className="flex-1 min-w-0">
              <RichContent content={option.content} inline={option.content.length === 1} />
            </div>

            {indicator}
          </button>
        );
      })}
    </div>
  );
}
